import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    updateAgentEmail,
    updateAgentNumber,
} from "../features/detailsConfirmation/detailsConfirmationSlice";
import { apiRequest, getUserDetails, setUserDetails } from "../services/apiRequest";
import { requestType } from "../services/requests";
import translationResources from "./multiLanguage";

// Event handlers to prevent default behavior
const preventDefault = (e) => {
    e.preventDefault();
};

const preventDefaultForScrollKeys = (e) => {
    // 33 - up arrow, 34 - down arrow, 35 - end, 36 - home
    const keys = [33, 34, 35, 36];
    if (keys.includes(e.keyCode)) {
        e.preventDefault();
    }
};

// Disable window scroll
export const onDisableWindowScroll = () => {
    window.addEventListener("wheel", preventDefault, { passive: false });
    window.addEventListener("touchmove", preventDefault, { passive: false });
    window.addEventListener("keydown", preventDefaultForScrollKeys, { passive: false });
};

// Enable window scroll
export const onEnableWindowScroll = () => {
    window.removeEventListener("wheel", preventDefault);
    window.removeEventListener("touchmove", preventDefault);
    window.removeEventListener("keydown", preventDefaultForScrollKeys);
};

export const onOpenLinkInNewTab = (link) => {
    window.open(link, "_blank", "noopener, noreferrer");
};

// Hook
export const useOnClickOutside = (ref, handler) => {
    useEffect(
        () => {
            const listener = (event) => {
                // Do nothing if clicking ref's element or descendent elements
                if (!ref.current || ref.current.contains(event.target)) {
                    return;
                }
                handler(event);
            };
            document.addEventListener("mousedown", listener);
            document.addEventListener("touchstart", listener);
            return () => {
                document.removeEventListener("mousedown", listener);
                document.removeEventListener("touchstart", listener);
            };
        },
        // Add ref and handler to effect dependencies
        // It's worth noting that because passed in handler is a new ...
        // ... function on every render that will cause this effect ...
        // ... callback/cleanup to run every render. It's not a big deal ...
        // ... but to optimize you can wrap handler in useCallback before ...
        // ... passing it into this hook.
        [ref, handler]
    );
};

// Handle resize observer (when the page has a video)
export const useHandleResizeObserver = () => {
    useEffect(() => {
        function hideError(e) {
            if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
                const resizeObserverErrDiv = document.getElementById(
                    "webpack-dev-server-client-overlay-div"
                );
                const resizeObserverErr = document.getElementById(
                    "webpack-dev-server-client-overlay"
                );
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute("style", "display: none");
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute("style", "display: none");
                }
            }
        }

        window.addEventListener("error", hideError);
        return () => {
            window.removeEventListener("error", hideError);
        };
    }, []);
};

// check if user_uid is included in array, if yes, we have to show the popup before the user clicks 'get started'
export const checkUserTypeToShowPopup = (arrayToCheck, url, isBuildYourHoliday = false) => {
    const userDetails = getUserDetails();

    const userUidHavingPopup = [59]; // TUI uid
    const isNordics = userDetails?.user_details?.country === "NORDICS";

    const isIncluded = userUidHavingPopup.includes(arrayToCheck) || isNordics;

    if (!isBuildYourHoliday) {
        if (isIncluded && url === "/get-started/1") {
            return true;
        }
    } else {
        if (isIncluded && url === "/create-your-holiday/1") {
            return true;
        }
    }

    return false;
};

// check if user_uid is included in array, if yes, handle the agent and email on microsites
export const handleAgentNameAndEmail = async (
    dispatch,
    userUidToCheck,
    customerHolidaysItinerariesData
) => {
    const userUidsArray = [59]; // TUI uid

    // const isIncluded = userUidsArray.includes(userUidToCheck);
    const isIncluded =
        userUidsArray.includes(userUidToCheck) ||
        (customerHolidaysItinerariesData.proposal_info === 2 &&
            customerHolidaysItinerariesData.country === "NORDICS");

    if (isIncluded) {
        //check if is tui uid
        if (customerHolidaysItinerariesData?.agent_store_contact_number) {
            dispatch(
                updateAgentNumber(customerHolidaysItinerariesData?.agent_store_contact_number)
            );
        } else {
            dispatch(updateAgentNumber(customerHolidaysItinerariesData?.agent_contact_number));
        }

        if (customerHolidaysItinerariesData?.agent_store_email) {
            dispatch(updateAgentEmail(customerHolidaysItinerariesData?.agent_store_email));
        } else {
            dispatch(updateAgentEmail(customerHolidaysItinerariesData?.agent_email));
        }
    } else {
        dispatch(updateAgentNumber(customerHolidaysItinerariesData?.agent_contact_number));
        dispatch(updateAgentEmail(customerHolidaysItinerariesData?.agent_email));
    }
};

// check if travel_agency_uid is included in array, if yes, allow user to upload the logo
// 30 -> INTELETRAVEL UK LTD, 45 -> Stewart Travel, 524 -> Stewart Travel LTD
export const checkTravelAgencyUidToUploadLogo = (travelAgencyUid) => {
    const userAllowedToUploadLogo = [30, 45, 524];
    const isIncluded = userAllowedToUploadLogo.includes(travelAgencyUid);

    if (isIncluded) {
        return true;
    }
    return false;
};

// check if travel_agency_uid is included in array, if yes, allow user to select logo on step 2 (sign up)
// 45 -> Stewart Travel, 524 -> Stewart Travel LTD
export const checkTravelAgencyUidToSelectLogo = (travelAgencyUid) => {
    const userAllowedToSelectLogo = [45, 524];
    const isIncluded = userAllowedToSelectLogo.includes(Number(travelAgencyUid));

    if (isIncluded) {
        return true;
    }
    return false;
};

//Check and Get current language
export const checkCurrentLanguage = (location) => {
    const lang = "es";
    let currentLanguage = localStorage.getItem("mrgCurrentLanguage");

    if (!currentLanguage) {
        currentLanguage = "en"; // default is English
    }

    if (window.location.hostname && window.location.hostname.endsWith(`.${lang}`)) {
        currentLanguage = lang;
    } else {
        currentLanguage = "en"; // otherwise, default to English
    }

    // Match "es" as a whole path segment, not a substring: share codes are random, so
    // `/your-cruise/public/essVCIpF0l` would otherwise flip the app to Spanish.
    // Segment rather than suffix because nested routes put it mid-path
    // (`/royal-bridge/commercial-performance/es/pipeline`).
    if (location.pathname.split("/").includes(lang)) {
        currentLanguage = lang; // switch to Spanish if "/es" is in the pathname
    } else {
        currentLanguage = "en"; // otherwise, default to English
    }

    localStorage.setItem("mrgCurrentLanguage", currentLanguage);
    window.currentLanguage = currentLanguage;
};

// Force the active language, for the cases where the user deliberately leaves a
// locale rather than arriving at one via the URL. Both writes are required:
// `handlePathsBasedOnLanguage` reads the stored value on every route change and
// re-appends the `/es` suffix, while `getCurrentTranslations` reads the window one.
export const setCurrentLanguage = (language) => {
    localStorage.setItem("mrgCurrentLanguage", language);
    window.currentLanguage = language;
};

//get current translations
// export const getCurrentTranslations = (keyPage, translationKey) => {
//     return translationResources[keyPage][window.currentLanguage][translationKey];
// };

// get current translations
export const getCurrentTranslations = (
  keyPage,
  translationKey,
  params = {},
  lang = window.currentLanguage
) => {
  let text =
    translationResources?.[keyPage]?.[lang]?.[translationKey] ?? '';

  Object.keys(params).forEach((key) => {
    text = text.replace(
      new RegExp(`\\{${key}\\}`, 'g'),
      params[key]
    );
  });

  return text;
};


// redirect to myroyalgenie.com/es
export const redirectToComWithEsPath = (location) => {
    const lang = "es";
    // Check if the current hostname ends with ".es"
    if (window.location.hostname) {
        if (window.location.hostname.endsWith(`.${lang}`)) {
            // Construct the new URL by replacing ".es" with ".com" and appending "/es" to the path
            const newHostname = window.location.hostname.replace(`.${lang}`, ".com");
            const newPathname =
                window.location.pathname === "/"
                    ? `${window.location.pathname}${lang}`
                    : `${window.location.pathname}/${lang}`;
            const newUrl = `${window.location.protocol}//${newHostname}${newPathname}`;

            // Redirect to the new URL
            window.location.replace(newUrl); // Using replace() to avoid adding to history
        }
    }
};

//Handle paths based on language
export const handlePathsBasedOnLanguage = (location, navigate, callback) => {
    let currentLanguage = localStorage.getItem("mrgCurrentLanguage");

    if (currentLanguage && currentLanguage !== "en") {
        console.log("************ currentLanguage: ", currentLanguage);

        //redirectToComWithEsPath(location);

        const currentPath = location.pathname;

        // Check if the path already ends with the language suffix (e.g., '/es')
        if (!currentPath.endsWith(`/${currentLanguage}`)) {
            // const newPath = `${currentPath}/${currentLanguage}`;
            // navigate(newPath, { replace: true });

            // Handle cases where the path is just the root ('/')
            const trimmedPath = currentPath === "/" ? "" : currentPath;

            const newPath = `${trimmedPath}/${currentLanguage}`;
            navigate(newPath, { replace: true });
        }
    }

    if (callback) {
        callback();
    }
};
// End ./ Check and Get current language

//Remove Aquatheater from ICON Oft the Seas (ship uid: 8128), based in title and category(en and es)
export const removeAquatheater = (shipUid, title, category) => {
    if (shipUid == 4) {
        if (
            title.toLocaleLowerCase() == "AQUATHEATRE SHOWS".toLocaleLowerCase() &&
            category.toLocaleLowerCase() == "entertainment".toLocaleLowerCase()
        ) {
            return true;
        } else if (
            title.toLocaleLowerCase() === "ESPECTÁCULOS EN EL AQUATHEATRE".toLocaleLowerCase() &&
            (category.toLocaleLowerCase() == "ESPECTÁCULOS".toLocaleLowerCase() ||
                category.toLocaleLowerCase() == "entertainment".toLocaleLowerCase())
        ) {
            return true;
        }
    }
    return false;
};

//Check if the user is part of agnecy: Royal International - uid 370
export const checkIfIsRoyalInternational = (agencyUid) => {
    if (agencyUid == 370) {
        return true;
    }
    return false;
};

export const sortSuitcases = (suitcases, sortBy, sortOrder = "asc") => {
    return [...suitcases].sort((a, b) => {
        let valueA, valueB;

        switch (sortBy) {
            case "customer_name":
                // String comparison for names
                valueA = a.customer_name.toLowerCase();
                valueB = b.customer_name.toLowerCase();
                break;

            case "tstamp_create":
                // Assuming date is in "DD/MM/YY" format
                // Convert to "YYYY/MM/DD" for proper date comparison
                const [dayA, monthA, yearA] = a.tstamp_create.split("/");
                const [dayB, monthB, yearB] = b.tstamp_create.split("/");
                valueA = new Date(`20${yearA}-${monthA}-${dayA}`);
                valueB = new Date(`20${yearB}-${monthB}-${dayB}`);
                break;

            case "lead_status":
                // Define custom priority for status values
                const statusPriority = {
                    "hot_lead": 1,
                    "warm_lead": 2,
                    "no_lead": 3,
                };
                valueA = statusPriority[a.lead_status] || 4;
                valueB = statusPriority[b.lead_status] || 4;
                break;

            case "cruise":
                // String comparison for names
                valueA = a.cruise.toLowerCase();
                valueB = b.cruise.toLowerCase();
                break;

            default:
                return 0;
        }

        // Handle comparison
        let comparison = 0;

        if (valueA < valueB) {
            comparison = -1;
        } else if (valueA > valueB) {
            comparison = 1;
        }

        // Apply sort order
        return sortOrder === "desc" ? comparison * -1 : comparison;
    });
};

export const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export const contactUsLegendData = {
    data: {
        agent_name: "Royal Caribbean",
        agent_contact_number: "+44-0344-493-4005",
        agent_email: "www.royalcaribbean.co.uk",
    },
};
